import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus, Rss, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { FadeUp, MaskReveal } from "@/components/Motion";
import useSEO, { canonicalFor } from "@/lib/useSEO";

const FIGURE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3e477c2a-f98b-44c6-92b8-bae42dd1c756/images/99b1a542992bdd4e6d6ac6dfb6bbb72c1daa2b015a6bb9893ced7c58a561ed22.png";

const PAGE_SIZE = 6;

export default function Insights() {
  useSEO({
    title: "Newsroom — Corpora Consultants",
    description: "Corporate law and regulatory briefings on Cyprus, the EU, MiCA, MiFID II, tax and cross-border operations. Auto-updated twice weekly.",
    canonical: canonicalFor("/insights"),
  });

  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setLoading(true);
    api
      .get("/insights")
      .then((r) => setPosts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [i18n.language]);

  const allLabel = t("insights.filterAll");

  const categories = useMemo(() => {
    const set = new Set(posts.map((p) => p.category));
    return [allLabel, ...Array.from(set)];
  }, [posts, allLabel]);

  const filtered = useMemo(() => {
    let list = posts;
    if (filter !== "All" && filter !== allLabel) {
      list = list.filter((p) => p.category === filter);
    }
    const query = q.trim().toLowerCase();
    if (query) {
      list = list.filter((p) => {
        const hay = [
          p.title,
          p.excerpt,
          p.category,
          p.author,
          Array.isArray(p.tags) ? p.tags.join(" ") : "",
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(query);
      });
    }
    return list;
  }, [filter, posts, allLabel, q]);

  // Reset pagination on filter/language/search change
  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [filter, i18n.language, q]);

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString(i18n.language || "en", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const featured = filtered[0];
  const rest = filtered.slice(1);
  const shownRest = rest.slice(0, visible);
  const hasMore = rest.length > shownRest.length;

  return (
    <div data-testid="insights-page">
      <section className="relative h-[60vh] min-h-[480px] overflow-hidden grain">
        <img
          src={FIGURE_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "brightness(0.45) grayscale(0.3)" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,11,13,0.65) 0%, rgba(10,11,13,0.4) 35%, rgba(10,11,13,0.95) 100%)",
          }}
        />
        <div className="relative z-10 h-full max-w-[1480px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-20">
          <div className="overline mb-6">{t("insights.eyebrow")}</div>
          <h1
            className="display text-[var(--bone)]"
            style={{ fontSize: "clamp(64px, 10vw, 160px)" }}
          >
            <div>
              <MaskReveal>{t("insights.heroLine1")}</MaskReveal>
            </div>
            <div className="display-italic" style={{ color: "var(--pewter)" }}>
              <MaskReveal delay={0.2}>{t("insights.heroLine2")}</MaskReveal>
            </div>
          </h1>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          {/* SEARCH */}
          <FadeUp>
            <div
              className="relative max-w-2xl mb-8"
              data-testid="insights-search-wrap"
            >
              <Search
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--bone-2)]/45 pointer-events-none"
              />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("insights.searchPlaceholder")}
                aria-label={t("insights.searchPlaceholder")}
                data-testid="insights-search"
                className="w-full bg-transparent border border-[var(--line-2)] focus:border-[var(--pewter)] outline-none transition-colors text-[var(--bone)] placeholder:text-[var(--bone-2)]/40 py-3.5 pl-11 pr-11 font-mono text-sm"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  aria-label={t("insights.clearSearch")}
                  data-testid="insights-search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[var(--bone-2)]/60 hover:text-[var(--bone)]"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </FadeUp>

          {/* FILTERS + COUNT */}
          <FadeUp>
            <div className="flex flex-wrap items-center justify-between gap-6 mb-14 border-b border-[var(--line)] pb-6">
              <div className="flex flex-wrap gap-3" data-testid="insights-filters">
                {categories.map((c) => {
                  const isActive =
                    (filter === "All" && c === allLabel) || filter === c;
                  return (
                    <button
                      key={c}
                      data-testid={`insights-filter-${c.toString().toLowerCase()}`}
                      onClick={() => setFilter(c === allLabel ? "All" : c)}
                      className={`tag ${isActive ? "tag-active" : ""}`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
              {!loading && filtered.length > 0 && (
                <div className="flex items-center gap-5">
                  <div
                    className="font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--mute-2)]"
                    data-testid="insights-count"
                  >
                    {t("insights.showingCount", {
                      shown: Math.min(filtered.length, 1 + shownRest.length),
                      total: filtered.length,
                    })}
                  </div>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/feed.xml`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="insights-rss-link"
                    title={t("insights.subscribeRss")}
                    className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--pewter)] hover:text-[var(--bone)]"
                  >
                    <Rss size={12} /> {t("insights.subscribeRss")}
                  </a>
                </div>
              )}
            </div>
          </FadeUp>

          {/* LOADING SKELETONS */}
          {loading && <InsightsSkeleton />}

          {!loading && filtered.length === 0 && (
            <div
              className="font-mono text-sm text-[var(--mute)] py-20 text-center"
              data-testid="insights-empty"
            >
              {q.trim()
                ? t("insights.searchEmpty", { q: q.trim() })
                : t("insights.empty")}
            </div>
          )}

          {/* FEATURED */}
          {featured && (
            <FadeUp>
              <Link
                to={`/insights/${featured.slug}`}
                data-testid="insights-featured"
                className="insight-card group block mb-20 border-b border-[var(--line)] pb-16"
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
                  <div className="md:col-span-7 overflow-hidden aspect-[16/10]">
                    <img
                      src={featured.cover_image}
                      alt={featured.title}
                      loading="lazy"
                      className="insight-img w-full h-full object-cover"
                    />
                  </div>
                  <div className="md:col-span-5">
                    <div className="overline mb-4">
                      {featured.category} · {t("insights.featured")}
                    </div>
                    <h2 className="display text-4xl md:text-5xl text-[var(--bone)] leading-[1.05] group-hover:text-[var(--pewter)] transition-colors">
                      {featured.title}
                    </h2>
                    <p className="mt-5 text-[var(--bone-2)] leading-relaxed text-lg">
                      {featured.excerpt}
                    </p>
                    <div className="mt-8 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--mute)]">
                      <span>{featured.author}</span>
                      <span>·</span>
                      <span>
                        {featured.read_minutes} {t("insights.minRead")}
                      </span>
                      <ArrowUpRight
                        size={16}
                        className="text-[var(--pewter)] ml-auto"
                      />
                    </div>
                  </div>
                </div>
              </Link>
            </FadeUp>
          )}

          {/* GRID — per-card FadeUp triggers reliably */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12"
            data-testid="insights-grid"
          >
            {shownRest.map((p, idx) => (
              <FadeUp key={p.id} delay={Math.min(idx, 5) * 0.06}>
                <Link
                  to={`/insights/${p.slug}`}
                  className="insight-card group block"
                  data-testid={`insight-card-${p.slug}`}
                >
                  <div className="overflow-hidden aspect-[4/3] mb-6">
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      loading="lazy"
                      className="insight-img w-full h-full object-cover"
                    />
                  </div>
                  <div className="overline mb-3">{p.category}</div>
                  <h3 className="display text-2xl md:text-3xl text-[var(--bone)] leading-[1.15] group-hover:text-[var(--pewter)] transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[var(--mute)] leading-relaxed line-clamp-3">
                    {p.excerpt}
                  </p>
                  <div className="mt-5 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--mute-2)]">
                    <span>
                      {p.read_minutes} {t("insights.min")}
                    </span>
                    <span>·</span>
                    <span>{fmtDate(p.created_at)}</span>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>

          {/* LOAD MORE */}
          {hasMore && (
            <div className="mt-20 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                data-testid="insights-load-more"
                className="btn-secondary inline-flex items-center gap-3"
              >
                <Plus size={14} />
                {t("insights.loadMore", {
                  remaining: Math.min(PAGE_SIZE, rest.length - shownRest.length),
                })}
              </button>
            </div>
          )}

          {!hasMore && filtered.length > PAGE_SIZE && (
            <div
              className="mt-20 text-center font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--mute-2)]"
              data-testid="insights-end"
            >
              {t("insights.endOfList")}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function InsightsSkeleton() {
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12"
      data-testid="insights-skeleton"
      aria-hidden
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i}>
          <div
            className="aspect-[4/3] mb-6 skeleton"
            style={{ animationDelay: `${i * 0.08}s` }}
          />
          <div
            className="skeleton h-3 w-20 mb-4"
            style={{ animationDelay: `${i * 0.08 + 0.05}s` }}
          />
          <div
            className="skeleton h-7 w-5/6 mb-3"
            style={{ animationDelay: `${i * 0.08 + 0.1}s` }}
          />
          <div
            className="skeleton h-4 w-full mb-2"
            style={{ animationDelay: `${i * 0.08 + 0.15}s` }}
          />
          <div
            className="skeleton h-4 w-2/3"
            style={{ animationDelay: `${i * 0.08 + 0.2}s` }}
          />
        </div>
      ))}
    </div>
  );
}
